/**
 * THE STAMP BAND — the reward landed on the thing you acted on.
 *
 * "Done · +5" is read where the tap happened. A toast is a receipt for an event that has already
 * gone by the time you look up; a stamp is the answer arriving on the card, held long enough to
 * read, and then the card leaves. That order is the whole component: stamp, hold, leave — never
 * leave and then announce.
 *
 * Two pieces, and a caller needs both:
 *
 *   `StampBand`   the overlay — a card-coloured scrim with one full-width band across its middle.
 *                 `pointerEvents="none"`: the card underneath stays exactly where it is and
 *                 answers nothing for the hold, so a second tap cannot land on a card that is
 *                 already leaving.
 *   `Stamped`     the wrapper that runs the timing and takes the card out. It measures the card
 *                 once, holds `STAMP_HOLD_MS`, then animates opacity and height to nothing and
 *                 calls back — and under reduced motion it holds and then removes with no
 *                 animation at all, which is the same information without the movement.
 *
 * It is never used for an error. A stamp is a reward; a failure is an Alert. And the stamp is only
 * ever shown once the action's request has SUCCEEDED — a reward for something the server has not
 * agreed to is a lie the next refresh corrects.
 */
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { useReducedMotion } from '@/lib/motion';
import { useStore } from '@/store';
import * as React from 'react';
import { Animated, Platform, View } from 'react-native';

/** How long the band holds the card before it leaves. Long enough to read six characters. */
export const STAMP_HOLD_MS = 700;

/** How long the card takes to go, once the hold is over. */
const LEAVE_MS = 240;

/**
 * THE GOLD GRADIENT — lit from the top edge, and held once for the whole app.
 *
 * Bouquet's stamp band and the Scoreboard's sealed sticker are the same struck gold, so they read
 * the same string: two copies of a gradient are two things to forget to change together, and they
 * had already drifted on their stop percentages.
 *
 * `hsl(var(--gold-hot))` into `hsl(var(--gold))`, the way `covered-picture.tsx` reads the round's
 * gradient pair, rather than the two hexes those tokens resolve to — a colour written twice is a
 * colour the next retheme moves in one place. Web only, and that is what makes it safe: a
 * `linear-gradient` is a CSS value with no React Native equivalent, so native takes the flat
 * `bg-gold` underneath it either way, and Tuxedo's band is flat and never reaches this at all.
 */
export const GOLD_GRADIENT = 'linear-gradient(180deg, hsl(var(--gold-hot)) 0%, hsl(var(--gold)) 100%)';

/** What a stamp says. `points` is the reward; a skip has none and takes the muted band. */
export interface Stamp {
  kind: 'done' | 'skipped';
  points?: number;
  /**
   * The band's words, where "Done · +N" is not what happened.
   *
   * Signals' approval is the one other thing in the app that is a MOMENT landing on the card the
   * tap happened on — "Approved · 3 steps in flight" — and it carries no points, because nothing
   * vulnerable is ever scored. Still a reward band and still never an error: the rule at the top
   * of this file is about what a stamp is FOR, not about which six characters it prints.
   */
  label?: string;
  /**
   * One control ON the band — in practice the single word "Undo".
   *
   * The band is otherwise inert by design (`pointerEvents="none"`, so a second tap cannot land on
   * a card that is already leaving), and an action is the one thing worth breaking that for: the
   * hold is exactly the window in which somebody realises they pressed the wrong grade of no, and
   * an undo anywhere else on the screen would be an undo they have to go and find. Handed one, the
   * overlay becomes solid rather than transparent to touches — it ABSORBS the taps it used to let
   * through, so the card underneath is no more reachable than it was.
   */
  action?: React.ReactNode;
}

/**
 * The overlay. Absolutely positioned across the card's content, so the card's own layout is
 * untouched and the band lands wherever the card already is.
 *
 * Bouquet centres its band and Tuxedo sits it on the foot — the two sheets' own placements, and
 * the one thing here that reads the look.
 */
export function StampBand({ kind, points, label, action, className }: Stamp & { className?: string }) {
  const look = useStore((s) => s.look);
  const tuxedo = look === 'tuxedo';
  const done = kind === 'done';

  return (
    <View
      pointerEvents={action ? 'auto' : 'none'}
      className={cn('absolute inset-0 items-center', tuxedo ? 'justify-end' : 'justify-center', className)}>
      {/* A scrim rather than an opaque cover: the card stays legible behind its own verdict.
          Plain `opacity` rather than a `bg-card/80` alpha modifier — the generated colour tokens
          are `hsl(var(--card))` with no `<alpha-value>` slot, so the modifier is a gamble on how
          the build rewrites it and this is the same pixels either way. */}
      <View className="bg-card absolute inset-0" style={{ opacity: tuxedo ? 0.66 : 0.72 }} />

      {done ? (
        <View
          className={cn('bg-gold border-gold-edge w-full items-center', tuxedo ? 'border-t py-5' : 'border-y py-3')}
          /* The gradient is web-only and Bouquet-only: `bg-gold` is the flat fill Tuxedo wants and
             the one native takes, and the lit version is painted over it where CSS exists. */
          style={tuxedo || Platform.OS !== 'web' ? undefined : ({ backgroundImage: GOLD_GRADIENT } as never)}>
          <Text
            variant="heading"
            className={cn(
              'text-gold-ink font-bold',
              tuxedo ? 'text-[18px] leading-[24px] tracking-[1.8px]' : 'italic text-[21px] leading-[26px] tracking-[0.84px]',
            )}>
            {label ?? `Done · +${points ?? 0}`}
          </Text>
        </View>
      ) : (
        /* A skip keeps the muted band: the boards draw no stamp for one, and inventing a gold one
           for a decline would be paying somebody for saying no.

           With an action it is a row rather than a centred word — the verdict on the left, the way
           out of it on the right — because a control centred under "Skipped" reads as a second
           label rather than as the thing to press. */
        <View
          className={cn(
            'bg-muted w-full py-3',
            action ? 'flex-row items-center justify-between gap-3 px-4' : 'items-center',
          )}>
          <Text variant="heading" className="text-muted-foreground">
            {label ?? 'Skipped'}
          </Text>
          {action}
        </View>
      )}
    </View>
  );
}

/**
 * The card, and its exit.
 *
 * `stamp` null is the ordinary case and costs nothing: the children render inside a plain View and
 * no measuring, animating or timing happens at all. Handed a stamp, this measures the card's
 * height once, holds, then collapses opacity and height together and calls `onLeft` — which is
 * where the caller reloads the list the card is about to be absent from.
 *
 * Height and not `transform`, deliberately: the rows below have to close the gap, or the card
 * leaves a hole that fills a beat later when the refresh lands, which reads as two events.
 */
export function Stamped({
  stamp,
  onLeft,
  holdMs,
  children,
}: {
  stamp: Stamp | null;
  /** Called once the card is out of the way. The caller reloads here. */
  onLeft: () => void;
  /**
   * How long the band holds, where `STAMP_HOLD_MS` is the wrong length.
   *
   * It is the wrong length in exactly one case: a band carrying an `action`. 700ms is measured to
   * be long enough to READ six characters, and an undo window has to be long enough to notice the
   * mistake, decide, and reach the word — which is several times that. Every other stamp in the
   * app leaves this alone and gets the one shared beat.
   */
  holdMs?: number;
  children: React.ReactNode;
}) {
  const reduced = useReducedMotion();
  const [height, setHeight] = React.useState<number | null>(null);
  const anim = React.useRef(new Animated.Value(1)).current;
  // Refs, so the effect below depends on the stamp and the measurement and nothing else.
  const left = React.useRef(onLeft);
  left.current = onLeft;

  React.useEffect(() => {
    if (!stamp) {
      anim.setValue(1);
      return;
    }
    let live = true;
    const hold = setTimeout(() => {
      if (!live) return;
      // Reduced motion, or a card that never got measured (it was never laid out): hold, then go.
      if (reduced || height === null) {
        left.current();
        return;
      }
      Animated.timing(anim, { toValue: 0, duration: LEAVE_MS, useNativeDriver: false }).start(() => {
        if (live) left.current();
      });
    }, holdMs ?? STAMP_HOLD_MS);
    return () => {
      live = false;
      clearTimeout(hold);
    };
  }, [stamp, reduced, height, anim, holdMs]);

  if (!stamp) {
    return <View onLayout={(e) => setHeight(e.nativeEvent.layout.height)}>{children}</View>;
  }

  return (
    <Animated.View
      style={
        reduced || height === null
          ? undefined
          : {
              opacity: anim,
              height: anim.interpolate({ inputRange: [0, 1], outputRange: [0, height] }),
              overflow: 'hidden',
            }
      }>
      {children}
      <StampBand kind={stamp.kind} points={stamp.points} label={stamp.label} action={stamp.action} />
    </Animated.View>
  );
}
